import React, { useState, useEffect } from 'react';
import baseUrl from '../../config';

function Attendance() {
  const [attendanceData, setAttendanceData] = useState([]);
  const [totalPresent, setTotalPresent] = useState(0);
  const [totalAbsent, setTotalAbsent] = useState(0);
  const [totalTardy, setTotalTardy] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${baseUrl}api/students/performance`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Fetched data:", data); // This will still show the object

        // --- IMPORTANT CHANGE HERE ---
        // Access the 'attendance' array from the fetched data object
        const attendanceRecords = data.attendance; 

        if (!Array.isArray(attendanceRecords)) {
          throw new Error("Attendance data is not an array after accessing 'attendance' property.");
        }
        // --- END IMPORTANT CHANGE ---

        let presentCount = 0;
        let absentCount = 0;
        let tardyCount = 0;

        const processedData = attendanceRecords.map(record => { // Map over attendanceRecords
          let displayStatus = record.status;
          let remarks = '';

          // Adjust this logic based on your actual API response for status
          if (record.status === 'present') {
            presentCount++;
            displayStatus = 'Present';
          } else if (record.status === 'absent') {
            absentCount++;
            displayStatus = 'Absent';
          } else if (record.status === 'tardy') {
            tardyCount++;
            displayStatus = 'Tardy';
          }
          // Handle other statuses like 'pending' if they shouldn't count towards present/absent/tardy totals immediately

          const classDate = new Date(record.classSchedule.classDate);
          const formattedDate = classDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
          
          return {
            date: formattedDate,
            teacher: record.teacher || 'N/A', // Placeholder, ideally fetch teacher names
            class: record.classSchedule.time || 'N/A', // Placeholder, ideally fetch class names/subjects
            status: displayStatus,
            remarks: remarks,
            dayOfWeek: record.classSchedule.dayOfWeek
          };
        });

        setAttendanceData(processedData);
        setTotalPresent(presentCount);
        setTotalAbsent(absentCount);
        setTotalTardy(tardyCount);

      } catch (err) {
        setError(err.message);
        console.error("Failed to fetch attendance data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceData();
  }, [baseUrl]);

  return (
    <div className="min-h-screen font-inter">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header Section */}
        <header className="bg-white rounded-3xl shadow-xl p-2 md:p-3 border-b-8 border-indigo-600 flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <div>
            <h1 className="text-4xl md:text-2xl font-extrabold text-gray-800 mb-2 leading-tight">
              Attendance History
            </h1>
            <p className="text-sm md:text-xl text-gray-700">Overview of your class attendance records.</p>
          </div>
        </header>

        {/* Attendance Summary Cards */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white p-6 rounded-2xl shadow-lg flex items-center transform-gpu transition duration-300 ease-in-out hover:scale-105 hover:shadow-xl">
            <div className="p-4 bg-white bg-opacity-20 rounded-full mr-4 shadow-inner">
              <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            </div>
            <div>
              <p className="text-gray-100 text-base font-semibold">Total Present</p>
              <p className="text-4xl font-bold">{totalPresent}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-500 to-rose-600 text-white p-6 rounded-2xl shadow-lg flex items-center transform-gpu transition duration-300 ease-in-out hover:scale-105 hover:shadow-xl">
            <div className="p-4 bg-white bg-opacity-20 rounded-full mr-4 shadow-inner">
              <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            </div>
            <div>
              <p className="text-gray-100 text-base font-semibold">Total Absent</p>
              <p className="text-4xl font-bold">{totalAbsent}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-yellow-500 to-amber-600 text-white p-6 rounded-2xl shadow-lg flex items-center transform-gpu transition duration-300 ease-in-out hover:scale-105 hover:shadow-xl">
            <div className="p-4 bg-white bg-opacity-20 rounded-full mr-4 shadow-inner">
              <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            </div>
            <div>
              <p className="text-gray-100 text-base font-semibold">Total Tardy</p>
              <p className="text-4xl font-bold">{totalTardy}</p>
            </div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="flex flex-col sm:flex-row gap-4 mb-8">
          <input
            type="text"
            placeholder="Search by class or teacher..."
            className="flex-1 p-3 rounded-xl border border-gray-300 focus:ring-4 focus:ring-indigo-300 focus:border-indigo-500 transition duration-300 ease-in-out shadow-sm"
          />
          <select className="p-3 rounded-xl border border-gray-300 focus:ring-4 focus:ring-indigo-300 focus:border-indigo-500 transition duration-300 ease-in-out shadow-sm">
            <option value="">Filter by Month</option>
            <option value="jan">January</option>
            <option value="feb">February</option>
            <option value="mar">March</option>
            <option value="apr">April</option>
            <option value="may">May</option>
            <option value="jun">June</option>
            <option value="jul">July</option>
            <option value="aug">August</option>
            <option value="sep">September</option>
            <option value="oct">October</option>
            <option value="nov">November</option>
            <option value="dec">December</option>
          </select>

          <button className="bg-indigo-600 text-white p-3 rounded-xl shadow-lg hover:bg-indigo-700 transition duration-300 ease-in-out flex items-center justify-center transform-gpu hover:-translate-y-0.5">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
            Search
          </button>
        </section>

        {/* Detailed Records Table */}
        <section className="bg-white rounded-3xl shadow-xl p-6 md:p-8 border-b-8 border-blue-600">
          <h3 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-6">Detailed Records</h3>
          {loading && <p className="text-center text-gray-600">Loading attendance data...</p>}
          {error && <p className="text-center text-red-600">Error: {error}</p>}
          {!loading && !error && attendanceData.length === 0 && (
            <p className="text-center text-gray-600">No attendance records found.</p>
          )}
          {!loading && !error && attendanceData.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-inner">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-50">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider rounded-tl-xl">Date</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Class Time</th>
                    {/* <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Teacher</th> */}
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider rounded-tr-xl">Remarks</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {attendanceData.map((record, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{record.dayOfWeek} - {record.class}</td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{record.teacher}</td> */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full
                          ${record.status === 'Present' ? 'bg-green-100 text-green-800' : ''}
                          ${record.status === 'Absent' ? 'bg-red-100 text-red-800' : ''}
                          ${record.status === 'Tardy' ? 'bg-yellow-100 text-yellow-800' : ''}
                        `}>
                          {record.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{record.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default Attendance;